'use client';

import {
  Button,
  Modal,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalIcon,
  ModalTitle,
} from '@lulalife/design-system';

type SettingsDiscardModalProps = {
  open: boolean;
  /** Leave, throwing the draft away. */
  onConfirm: () => void;
  /** Stay on the page with the draft intact. Also the overlay/Escape path. */
  onCancel: () => void;
  'data-testid'?: string;
};

/**
 * The confirm the prototype opens when you try to leave a settings tab with
 * unsaved changes (`guardNav`).
 *
 * Every part is the design system's — `Modal` and friends ship in 0.3.1 — so this
 * is a composition, not a stand-in, and carries no DS-GAP marker. It exists to
 * hold the prototype's exact copy and the danger styling in one place rather
 * than restating both at each guarded surface.
 *
 * COPY IS THE PROTOTYPE'S, verbatim: the title, the body sentence and the
 * "Discard changes" confirm label all come from its `showModal` call. The
 * destructive action is the one being confirmed, so it takes the danger variant
 * and Cancel is the safe default.
 *
 * `data-lula-ds` GOES ON `Modal` ITSELF, not a wrapper: the DS `Modal` renders
 * via `createPortal(..., document.body)`, so its content mounts OUTSIDE every
 * `DSSurface`/`SettingsRow`-marked subtree no matter where this component sits
 * in the tree. `design-system-compat.css`'s guards would otherwise strip the
 * footer Buttons' padding (`.button { padding-block/inline: initial }`), the
 * built-in close button's, and the warning icon's, because none of them are
 * inside a marked ancestor. `Modal` spreads its extra props onto the dialog
 * panel div (`role="dialog"`), so the attribute passed here lands exactly where
 * it needs to, on the one element every rendered part is a descendant of.
 */
export const SettingsDiscardModal = ({
  open,
  onConfirm,
  onCancel,
  'data-testid': dataTestId,
}: SettingsDiscardModalProps) => (
  <Modal
    open={open}
    onOpenChange={(nextOpen) => {
      if (!nextOpen) onCancel();
    }}
    size="small"
    variant="confirmation"
    showCloseButton={false}
    data-lula-ds=""
    data-testid={dataTestId}
  >
    {/* Figma "Discard changes · Confirmation": the icon beside one column holding the title and
        the body (primary text colour), a danger icon, and no close button. With no close button,
        the DS header's right gutter for it goes too. Figma measures from the dialog's outer edge
        and the DS's 1px border sits inside it, so these two values put the column at Figma's x72
        and 356 wide, 20 from the right (#428). */}
    <ModalHeader
      icon={<ModalIcon type="danger" />}
      className="md:gap-[11px]! md:pr-[19px]! md:[&>div:last-child]:pr-0"
    >
      <ModalTitle>Discard unsaved changes?</ModalTitle>
      <ModalDescription className="text-text-primary">
        You have unsaved changes on this page. If you leave, they will be discarded.
      </ModalDescription>
    </ModalHeader>
    <ModalFooter>
      <Button variant="secondary" type="button" onClick={onCancel}>
        Cancel
      </Button>
      <Button variant="destructive" type="button" onClick={onConfirm}>
        Discard changes
      </Button>
    </ModalFooter>
  </Modal>
);

export default SettingsDiscardModal;
